/**
 * BYK-17: Style Transfer - AI Material Palette Mapping
 *
 * Service for managing style palettes and applying them to Babylon.js materials.
 */

import type {
  StylePalette,
  StylePresetName,
  MaterialConfig,
  SurfaceType,
} from "@byk/shared";
import { STYLE_PALETTE_PRESETS as PRESETS } from "@byk/shared";
import * as BABYLON from "@babylonjs/core";

/**
 * Get all available style palette presets
 */
export function getStylePresets(): StylePalette[] {
  return Object.entries(PRESETS).map(([key, preset]) => ({
    id: key,
    ...preset,
  })) as StylePalette[];
}

/**
 * Get a specific preset by name
 */
export function getPresetByName(name: StylePresetName): StylePalette | null {
  const preset = PRESETS[name];
  if (!preset) return null;
  return {
    id: name,
    ...preset,
  } as StylePalette;
}

/**
 * Create a custom palette from a base preset with modifications
 */
export function createCustomPalette(
  baseName: StylePresetName,
  customizations: Partial<Record<SurfaceType, Partial<MaterialConfig>>>,
  name: string = "Custom"
): StylePalette {
  const base = getPresetByName(baseName);
  if (!base) {
    throw new Error(`Unknown preset: ${baseName}`);
  }

  const materials = { ...base.materials };
  for (const [surface, config] of Object.entries(customizations)) {
    if (materials[surface as SurfaceType]) {
      materials[surface as SurfaceType] = {
        ...materials[surface as SurfaceType],
        ...config,
      };
    }
  }

  return {
    id: `custom-${Date.now()}`,
    name,
    description: `Custom palette based on ${base.name}`,
    isPreset: false,
    materials,
    accentColor: base.accentColor,
  };
}

/**
 * Convert hex color to Babylon.js Color3
 */
function hexToColor3(hex: string): BABYLON.Color3 {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    return new BABYLON.Color3(0.5, 0.5, 0.5); // Default gray
  }
  return new BABYLON.Color3(
    parseInt(result[1], 16) / 255,
    parseInt(result[2], 16) / 255,
    parseInt(result[3], 16) / 255
  );
}

/**
 * Create a PBRMaterial from a MaterialConfig
 */
export function createPBRMaterial(
  name: string,
  config: MaterialConfig,
  scene: BABYLON.Scene
): BABYLON.PBRMaterial {
  const material = new BABYLON.PBRMaterial(name, scene);

  // Base color
  material.albedoColor = hexToColor3(config.baseColor);

  // PBR properties
  material.metallic = config.metallic;
  material.roughness = config.roughness;

  // Optional emissive
  if (config.emissiveColor) {
    material.emissiveColor = hexToColor3(config.emissiveColor);
  }

  // Enable environment reflections
  material.environmentIntensity = 0.8;

  // Ambient occlusion
  if (config.ambientOcclusionIntensity !== undefined) {
    material.ambientColor = new BABYLON.Color3(
      config.ambientOcclusionIntensity,
      config.ambientOcclusionIntensity,
      config.ambientOcclusionIntensity
    );
  }

  return material;
}

/**
 * Map mesh names to surface types for material application
 * This mapping should match the naming convention in 3D models
 */
const MESH_TO_SURFACE_MAP: Record<string, SurfaceType> = {
  // Cabinet parts
  cabinet_door: "cabinet-door",
  cabinet_body: "cabinet-body",
  door: "cabinet-door",
  drawer: "cabinet-door",
  frame: "cabinet-body",
  shelf: "cabinet-body",

  // Surfaces
  countertop: "countertop",
  counter: "countertop",
  worktop: "countertop",
  backsplash: "backsplash",
  splash: "backsplash",

  // Room
  floor: "flooring",
  wall: "wall",

  // Appliances
  appliance: "appliance",
  oven: "appliance",
  fridge: "appliance",
  dishwasher: "appliance",
  cooktop_body: "appliance",

  // Hardware
  handle: "hardware",
  knob: "hardware",
  hardware: "hardware",
  pull: "hardware",
};

/**
 * Determine the surface type for a mesh based on its name
 */
function getSurfaceTypeForMesh(meshName: string): SurfaceType | null {
  const lowerName = meshName.toLowerCase();

  // Check direct mappings first
  for (const [key, surfaceType] of Object.entries(MESH_TO_SURFACE_MAP)) {
    if (lowerName.includes(key)) {
      return surfaceType;
    }
  }

  return null;
}

/**
 * Apply a style palette to all meshes in a Babylon.js scene
 */
export function applyStylePalette(
  scene: BABYLON.Scene,
  palette: StylePalette
): Map<string, BABYLON.PBRMaterial> {
  const appliedMaterials = new Map<string, BABYLON.PBRMaterial>();

  // Create materials for each surface type
  const materialCache = new Map<SurfaceType, BABYLON.PBRMaterial>();
  for (const [surfaceType, config] of Object.entries(palette.materials)) {
    const material = createPBRMaterial(
      `${palette.id}_${surfaceType}`,
      config,
      scene
    );
    materialCache.set(surfaceType as SurfaceType, material);
  }

  // Apply materials to meshes
  for (const mesh of scene.meshes) {
    if (!mesh.name || mesh.name.startsWith("__")) continue;

    const surfaceType = getSurfaceTypeForMesh(mesh.name);
    if (surfaceType && materialCache.has(surfaceType)) {
      const material = materialCache.get(surfaceType)!;
      mesh.material = material;
      appliedMaterials.set(mesh.name, material);
    }
  }

  return appliedMaterials;
}

/**
 * Apply a style palette to specific meshes by ID
 */
export function applyPaletteToMeshes(
  scene: BABYLON.Scene,
  palette: StylePalette,
  meshIds: string[],
  surfaceTypeOverride?: SurfaceType
): void {
  for (const meshId of meshIds) {
    const mesh = scene.getMeshByName(meshId) || scene.getMeshById(meshId);
    if (!mesh) continue;

    const surfaceType =
      surfaceTypeOverride || getSurfaceTypeForMesh(mesh.name) || "cabinet-body";
    const config = palette.materials[surfaceType];

    if (config) {
      const material = createPBRMaterial(
        `${palette.id}_${meshId}`,
        config,
        scene
      );
      mesh.material = material;
    }
  }
}

/**
 * Create a preview swatch for a palette (returns CSS gradient string)
 */
export function createPalettePreviewGradient(palette: StylePalette): string {
  const colors = [
    palette.materials.countertop.baseColor,
    palette.materials["cabinet-door"].baseColor,
    palette.materials.backsplash.baseColor,
    palette.materials.hardware.baseColor,
  ];

  return `linear-gradient(135deg, ${colors
    .map((c, i) => `${c} ${i * 25}%, ${c} ${(i + 1) * 25}%`)
    .join(", ")})`;
}

/**
 * Generate color swatches for palette display
 */
export function getPaletteSwatches(
  palette: StylePalette
): Array<{ color: string; label: string }> {
  return [
    { color: palette.materials.countertop.baseColor, label: "Counter" },
    { color: palette.materials["cabinet-door"].baseColor, label: "Cabinets" },
    { color: palette.materials.backsplash.baseColor, label: "Backsplash" },
    { color: palette.materials.hardware.baseColor, label: "Hardware" },
    { color: palette.accentColor, label: "Accent" },
  ];
}
